import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ws } from "./api";
import { PageHeader } from "./ui";

const SUGGESTIONS = ["Which open deals are most at risk?", "Summarise my pipeline by stage", "What should I focus on this week?", "Which deals are closest to closing?"];

export default function AIAssistantPage() {
  const [session, setSession] = useState(() => localStorage.getItem("crm_ai_session") || "");
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef();
  useEffect(() => { if (session) ws(`/crm/workspace/ai/history?session_id=${session}`).then((r) => setMsgs(r.messages)).catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (!question || busy) return;
    setQ(""); setMsgs((m) => [...m, { role: "user", text: question }]); setBusy(true);
    try {
      const r = await ws("/crm/workspace/ai/ask", { method: "POST", body: { question, session_id: session || undefined } });
      setSession(r.session_id); localStorage.setItem("crm_ai_session", r.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: r.answer }]);
    } catch (e) { toast.error(e.message); setMsgs((m) => m.slice(0, -1)); }
    finally { setBusy(false); }
  };
  const reset = () => { localStorage.removeItem("crm_ai_session"); setSession(""); setMsgs([]); };
  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="ai-assistant-page">
      <PageHeader title="AI Assistant" subtitle="Ask about your own workspace's deals, contacts and tasks. Answers use only your organisation's data."
        actions={msgs.length > 0 && <Button variant="outline" onClick={reset} data-testid="ai-new-chat-btn"><RotateCcw className="h-4 w-4 mr-1" />New chat</Button>} />
      <div className="flex-1 overflow-y-auto bg-white rounded-xl border border-slate-200/80 p-4 sm:p-6 space-y-4" data-testid="ai-messages">
        {msgs.length === 0 && (
          <div className="text-center py-10">
            <div className="h-12 w-12 mx-auto rounded-xl bg-[#0B1530] text-[#E8A33D] flex items-center justify-center mb-4"><Sparkles className="h-5 w-5" /></div>
            <p className="text-sm text-slate-600 mb-5">Try one of these:</p>
            <div className="flex flex-wrap justify-center gap-2">{SUGGESTIONS.map((s, i) => <button key={s} onClick={() => ask(s)} className="text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:border-[#0B1530] hover:text-[#0B1530] transition-colors" data-testid={`ai-suggestion-${i}`}>{s}</button>)}</div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-message-${m.role}-${i}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#0B1530] text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="ai-thinking"><Loader2 className="h-3.5 w-3.5 animate-spin" />Thinking…</div>}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about your pipeline, contacts or tasks…" className="flex-1 h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:border-slate-400" data-testid="ai-question-input" />
        <Button type="submit" disabled={busy || !q.trim()} className="h-11 px-5 bg-[#0B1530] hover:bg-[#1a2650]" data-testid="ai-send-btn"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
